import type { GetStaticProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { CanvasProvider } from "client/contexts/ResizeEditor/Canvas";
import { Footer } from "components/modules/Layout/Footer/Footer";
import { FlipRotateTutorial } from "components/modules/Tutorial/FlipRotateTutorial/FlipRotateTutorial";
import Head from "next/head";
import { FlipContent } from "components/modules/Content/FlipContent/FlipContent";
import { Tools } from "components/modules/Layout/Tools/Tools";
import { translateFunc } from "client/utils/translateFunc";
import { commonLanguageFiles } from "client/config";
import { LanguageProvider } from "client/contexts/LanguageProvider";
import dynamic from "next/dynamic";
import {TrustPilotSection} from "components/modules/TrustPilot/TrustPilotSection";
import React from "react";

const Flip = dynamic<{}>(() => import("components/modules/Editors/Flip/Flip").then((mod) => mod.Flip));

const Page = () => {
    const title = translateFunc("page-title");
    const description = translateFunc("meta-description");

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content="/social.png" />
                <meta property="twitter:card" content="summary" />
                <meta property="twitter:title" content={title} />
                <meta property="twitter:description" content={description} />
            </Head>
            <div className="page-shadow max-w-1440">
                <CanvasProvider>
                    <Flip />
                </CanvasProvider>

                <div className="max-w-1440 border-x">
                    <FlipContent />
                    <FlipRotateTutorial />
                    <TrustPilotSection />
                    <Tools />
                    <Footer url="/flip-image" />
                </div>
            </div>
        </>
    );
};

const files = ["flip", "uploader", ...commonLanguageFiles];

const LanguageWrapper = () => (
    <LanguageProvider files={files}>
        <Page />
    </LanguageProvider>
);

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
    props: {
        ...(await serverSideTranslations(locale ?? "", files)),
    },
});

export default LanguageWrapper;
